/**
 * Self-Referral Wizard Component
 *
 * Main wizard component that manages the multi-step self-referral workflow.
 * Handles step navigation, progress tracking, loading states, and error handling.
 * Integrates with existing UI components and theme system.
 * Follows the exact pattern from PatientRegistrationWizard.
 *
 * @module components/self-referral/SelfReferralWizard
 */

import React, { useEffect } from 'react';
import { Card, CardBody } from '@/components/ui/Card';
import { StepIndicator, type StepConfig } from '@/components/ui/StepIndicator';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';

// Step components
import { SelfReferralForm } from './SelfReferralForm';
import { DietitianSelectionStep } from './DietitianSelectionStep';
import { SelfReferralConfirmation } from './SelfReferralConfirmation';

// Types
import type {
  SelfReferralFormData,
  MatchedDietitian,
  WorkflowStep
} from './types';

export interface SelfReferralWizardProps {
  className?: string;
  providerId: string;
  workflowState: {
    currentStep: WorkflowStep;
    formData?: SelfReferralFormData;
    selectedDietitian?: MatchedDietitian;
    finalReferral?: { referral_token: string };
    sessionData?: { expires_at: string };
  };
  /** `undefined` = not yet fetched (loading); `[]` = fetched, empty (design-dietitian-accreditation §4.8) */
  matches?: MatchedDietitian[];
  isLoading: boolean;
  error: string | null;
  sessionToken?: string;
  onFormSubmit: (formData: SelfReferralFormData) => Promise<void>;
  onDietitianSelect: (dietitian: MatchedDietitian) => Promise<void>;
  onSessionExpire: () => void;
  onContinueToRegistration: () => void;
  onComplete?: (referralToken: string) => void;
  onStepChange?: (step: WorkflowStep) => void;
  onReset: () => void;
  onBackToForm?: () => void;
  onCreateNewReferral?: () => void;
}

// Simple icon components for steps
const FormIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const UserSelectIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const WIZARD_STEPS: StepConfig[] = [
  {
    key: 'form',
    title: 'Your Information',
    description: 'Tell us about your needs',
    icon: <FormIcon />
  },
  {
    key: 'selection',
    title: 'Select Dietitian',
    description: 'Choose your perfect match',
    icon: <UserSelectIcon />
  },
  {
    key: 'completed',
    title: 'Complete',
    description: 'Referral created successfully',
    icon: <CheckCircleIcon />
  }
];

/**
 * Step heading when the session returned no dietitians (design-dietitian-accreditation §4.8):
 * frames the hand-off card below without repeating it or saying "choose".
 */
const EMPTY_SELECTION_HEADING = {
  title: 'Next Steps',
  description: 'See below'
};

export function SelfReferralWizard(props: SelfReferralWizardProps) {
  // Hooks
  const { isDark } = useTheme();
  const [selectedDietitianId, setSelectedDietitianId] = React.useState<string | null>(null);

  // Destructure props for cleaner code
  const {
    workflowState,
    matches,
    isLoading,
    error,
    onFormSubmit,
    onDietitianSelect,
    onContinueToRegistration,
    onComplete,
    onStepChange,
    onBackToForm,
    onCreateNewReferral,
  } = props;

  const { currentStep } = workflowState;

  // undefined = still loading (spinner); [] = fetched and empty (hand-off card, §4.8)
  const isEmptySelection = currentStep === 'selection' && matches?.length === 0;

  // Handle dietitian selection (just highlight, don't proceed)
  const handleDietitianSelect = (dietitian: MatchedDietitian) => {
    setSelectedDietitianId(dietitian.dietician_id);
  };

  // Handle next button click (actually proceed with selection)
  const handleNext = async () => {
    // Only reachable from DietitianSelectionStep, which renders once matches is defined.
    const selectedDietitian = matches?.find(d => d.dietician_id === selectedDietitianId);
    if (selectedDietitian) {
      await onDietitianSelect(selectedDietitian);
    }
  };

  // Handle back button click
  const handleBack = () => {
    onBackToForm?.();
  };

  // Effect: Notify parent of step changes
  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  // Effect: Notify parent of completion
  useEffect(() => {
    if (currentStep === 'completed' && workflowState.finalReferral?.referral_token) {
      onComplete?.(workflowState.finalReferral.referral_token);
    }
  }, [currentStep, workflowState.finalReferral, onComplete]);

  // Step navigation handlers - disabled for self-referral as it's a linear flow
  const handleStepClick = () => {
    // Self-referral is a linear workflow, no manual step navigation allowed
    return;
  };

  // Get current step index for progress indicator
  const getCurrentStepIndex = () => {
    return WIZARD_STEPS.findIndex(step => step.key === currentStep);
  };

  // Render current step content
  const renderStepContent = (): React.ReactElement => {
    switch (currentStep) {
      case 'form':
        return (
          <SelfReferralForm
            onSubmit={onFormSubmit}
            isLoading={isLoading}
            error={error || undefined}
            initialData={workflowState.formData}
          />
        );

      case 'selection':
        // Loading only while matches is not yet fetched; [] falls through to the step's empty state
        if (matches === undefined) {
          return (
            <div className="text-center py-8">
              <p className={cn(
                "text-lg mb-4",
                isDark ? "text-secondary-400" : "text-secondary-600"
              )}>
                Finding your perfect dietitian matches...
              </p>
            </div>
          );
        }
        return (
          <DietitianSelectionStep
            dietitians={matches}
            onSelect={handleDietitianSelect}
            isLoading={isLoading}
            error={error || undefined}
            onBack={handleBack}
            onNext={handleNext}
            hasSelection={selectedDietitianId !== null}
          />
        );

      case 'completed': {
        if (!workflowState.finalReferral || !workflowState.selectedDietitian) {
          return (
            <div className="text-center py-8">
              <p className={cn(
                "text-lg",
                isDark ? "text-secondary-400" : "text-secondary-600"
              )}>
                Completing your referral...
              </p>
            </div>
          );
        }

        return (
          <SelfReferralConfirmation
            selectedDietitian={workflowState.selectedDietitian}
            onContinueToRegistration={onContinueToRegistration}
            onCreateNewReferral={onCreateNewReferral}
          />
        );
      }

      default:
        return <div>Invalid step</div>;
    }
  };

  // Main render
  return (
    <div className={cn('max-w-4xl mx-auto p-6', props.className)}>
      {/* Progress indicator */}
      <div className="mb-8">
        <StepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepClick={handleStepClick}
          variant="horizontal"
        />
      </div>

      {/* Main content card */}
      <div className="flex justify-center">
        <Card className={cn(
          'w-full max-w-2xl shadow-lg transition-all duration-300',
          isDark ? 'bg-secondary-900' : 'bg-white'
        )}>
          <CardBody className="p-8">
            {/* Step title and description */}
            <div className="mb-6">
              <h2 className={cn(
                'text-2xl font-bold mb-2',
                isDark ? 'text-secondary-100' : 'text-secondary-900'
              )}>
                {isEmptySelection
                  ? EMPTY_SELECTION_HEADING.title
                  : (WIZARD_STEPS[getCurrentStepIndex()]?.title || 'Loading...')}
              </h2>
              <p className={cn(
                'text-lg',
                isDark ? 'text-secondary-400' : 'text-secondary-600'
              )}>
                {isEmptySelection
                  ? EMPTY_SELECTION_HEADING.description
                  : (WIZARD_STEPS[getCurrentStepIndex()]?.description || 'Please wait...')}
              </p>
            </div>

            {/* Loading overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 rounded-lg">
                <div className={cn(
                  "p-4 rounded-lg shadow-lg",
                  isDark ? "bg-secondary-800" : "bg-white"
                )}>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className={cn(
                    "mt-2 text-sm text-center",
                    isDark ? "text-secondary-400" : "text-secondary-600"
                  )}>
                    {currentStep === 'form' ? 'Finding matches...' :
                     currentStep === 'selection' ? 'Creating referral...' :
                     'Processing...'}
                  </p>
                </div>
              </div>
            )}

            {/* Step content */}
            <div className="relative">
              {renderStepContent()}
            </div>

          </CardBody>
        </Card>
      </div>
    </div>
  );
}